import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useCreatePromptOverrideMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { AdminFormPage } from "@components/CatalogIndex";
import PromptOverrideForm from "../common/Form/Form";
import { INITIAL_FORM_STATE, PromptOverrideFormState } from "../common/types";
import { formatPromptOverrideData } from "../common/helpers";
import "../style.css";

const FORM_ID = "prompt-override-form";

const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [createPromptOverride] = useCreatePromptOverrideMutation();

  const [formState, setFormState] =
    useState<PromptOverrideFormState>(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const handleSave = () => {
    if (!formState.promptType || !formState.content) return;

    setLoading(true);
    const data = formatPromptOverrideData(formState);

    createPromptOverride({ slug: accountSlug, body: { data } })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        if (id) {
          navigate(`/admin/prompt_overrides/${id}/edit`, {
            state: { refetch: true },
          });
        } else {
          setErrorMessage("Failed to create prompt override.");
          setShowFailAlert(true);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        setErrorMessage(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
        setShowFailAlert(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <AdminFormPage
        title="Add a Prompt Override"
        formId={FORM_ID}
        onCancel={() => navigate("/admin/prompt_overrides")}
        isSubmitting={loading}
      >
        <PromptOverrideForm
          formId={FORM_ID}
          formState={formState}
          setFormState={setFormState}
          editing={false}
          onSubmit={handleSave}
        />
      </AdminFormPage>
    </>
  );
};

export default New;
